/**
 * JWT 토큰 디코딩 유틸리티
 */

export interface JwtPayload {
  sub: string // username
  userTokenId: string
  role: string
  iat: number
  exp: number
}

/**
 * JWT 토큰을 디코딩하여 페이로드를 반환
 * @param token JWT 토큰
 * @returns 디코딩된 페이로드 또는 null
 */
export function decodeJwt(token: string): JwtPayload | null {
  try {
    // JWT는 header.payload.signature 형식
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    // payload는 base64url로 인코딩되어 있음
    const payload = parts[1]

    // base64url → base64 변환
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')

    // base64 디코딩
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    return JSON.parse(jsonPayload) as JwtPayload
  } catch (error) {
    console.error('Failed to decode JWT:', error)
    return null
  }
}

/**
 * JWT 토큰에서 userTokenId 추출
 * @param token JWT 토큰
 * @returns userTokenId 또는 null
 */
export function extractUserTokenId(token: string): string | null {
  const payload = decodeJwt(token)
  return payload?.userTokenId || null
}

/**
 * JWT 토큰의 만료 여부 확인
 * @param token JWT 토큰
 * @returns 만료되었으면 true, 아니면 false
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token)
  if (!payload) {
    return true
  }

  const now = Math.floor(Date.now() / 1000)
  return payload.exp < now
}
